import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Briefcase, ArrowRight, Loader2, Crown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { getPaidJobStatus } from '@/lib/api';

const PostJobSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!sessionId) { setLoading(false); return; }

    let attempts = 0;
    const poll = async () => {
      try {
        const res = await getPaidJobStatus(sessionId);
        setStatus(res.data);
        // If payment_status is not paid yet and we're within budget, keep polling
        if (res.data?.payment_status !== 'paid' && attempts < 6) {
          attempts += 1;
          setTimeout(poll, 2000);
        } else {
          setLoading(false);
        }
      } catch (e) {
        console.error(e);
        setLoading(false);
      }
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <section className="pt-32 pb-20">
        <div className="max-w-lg mx-auto px-4">
          <Card className="shadow-lg" data-testid="post-job-success-card">
            <CardContent className="p-10 text-center">
              {loading ? (
                <>
                  <Loader2 className="h-12 w-12 animate-spin text-sky-500 mx-auto" />
                  <p className="text-slate-500 mt-4 text-sm">Confirming your payment...</p>
                </>
              ) : status?.payment_status === 'paid' ? (
                <>
                  <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="h-8 w-8 text-sky-600" />
                  </div>
                  <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2" data-testid="post-job-success-title">
                    Job Published!
                  </h1>
                  <p className="text-slate-600 mb-6">
                    Your job listing is now live on the Paisley Business Jobs Board for the next 30 days.
                    You&apos;ll also see it mirrored on paisley.org.uk shortly.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Link to="/jobs">
                      <Button className="bg-gradient-to-r from-sky-600 to-indigo-700 hover:from-sky-700 hover:to-indigo-800 text-white" data-testid="view-jobs-board-btn">
                        View Jobs Board <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    </Link>
                    <Link to="/">
                      <Button variant="outline" data-testid="back-to-home-btn">Back to Home</Button>
                    </Link>
                  </div>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 bg-sky-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Briefcase className="h-8 w-8 text-sky-600" />
                  </div>
                  <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">Processing Payment</h1>
                  <p className="text-slate-600 mb-6">
                    Your payment is being processed by Stripe. Your job will be published within a few seconds.
                    Refresh this page if it doesn&apos;t confirm shortly.
                  </p>
                  <Link to="/jobs">
                    <Button variant="outline">View Jobs Board</Button>
                  </Link>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default PostJobSuccessPage;
